import React from 'react';
import { useIntl } from 'react-intl';
import { Link } from './Link';
import { CompanyLogo } from './CompanyLogo';
import type { SrpTopEmployersStripItem } from '../types/srp-top-employers-strip';
import { prefetchBestCompaniesHubFromApi } from '../utils/best-companies/fetch-best-companies-hub-from-api';
import { getCompanyLogoDefaultSrc, getCompanyLogoSrcset } from '../utils/company/get-company-logo-url';
import { formatCompanyLogoAltLabelFromProps } from '../utils/company/format-company-logo-alt-label-from-props';

const CHIP_LOGO_PX = 28;

function isTyomarkkinatoriCompanyId(companyId: string): boolean {
  const n = companyId.toLowerCase().trim();
  return n === 'työmarkkinatori' || n === 'tyomarkkinatori';
}

type StripLogoPhase = 'hosted' | 'external' | 'fallback';

function SrpTopEmployerStripLogo({
  companyId,
  name,
  logoUrl,
}: {
  companyId: string;
  name: string;
  logoUrl?: string | null;
}): React.ReactElement {
  const intl = useIntl();
  const trimmedExternal = (logoUrl ?? '').trim();
  const isTyomarkkinatori = isTyomarkkinatoriCompanyId(companyId);
  const hostedDefaultSrc = React.useMemo(
    () => getCompanyLogoDefaultSrc(companyId, isTyomarkkinatori),
    [companyId, isTyomarkkinatori]
  );
  const hostedSrcset = React.useMemo(
    () => getCompanyLogoSrcset(companyId, isTyomarkkinatori),
    [companyId, isTyomarkkinatori]
  );

  const [phase, setPhase] = React.useState<StripLogoPhase>('hosted');
  const hostedImgRef = React.useRef<HTMLImageElement | null>(null);

  const alt = formatCompanyLogoAltLabelFromProps(intl, {
    initialsSourceName: name,
    companyId,
    companyName: name,
  });

  React.useEffect(() => {
    setPhase('hosted');
  }, [companyId, hostedDefaultSrc, trimmedExternal]);

  const goPastHosted = React.useCallback(() => {
    setPhase(trimmedExternal ? 'external' : 'fallback');
  }, [trimmedExternal]);

  /** Decode error / broken file: same idea as {@link CompanyLogo}’s delayed check for srcset images. */
  React.useEffect(() => {
    if (phase !== 'hosted' || !hostedSrcset) return;
    let cancelled = false;
    const t = window.setTimeout(() => {
      if (cancelled) return;
      const img = hostedImgRef.current;
      if (img?.complete && img.naturalWidth === 0 && img.naturalHeight === 0) {
        goPastHosted();
      }
    }, 2000);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [phase, hostedSrcset, goPastHosted, companyId, hostedDefaultSrc]);

  const onHostedLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      if (img.naturalWidth === 0 && img.naturalHeight === 0) {
        goPastHosted();
      }
    },
    [goPastHosted]
  );

  if (phase === 'fallback') {
    return (
      <CompanyLogo
        companyId={companyId}
        initialsSourceName={name}
        size={CHIP_LOGO_PX}
        alt={alt}
        variant="rounded"
        loading="lazy"
        className="!size-7 !border-0 !shadow-none"
      />
    );
  }

  if (phase === 'external' && trimmedExternal) {
    return (
      <img
        src={trimmedExternal}
        alt={alt}
        width={CHIP_LOGO_PX}
        height={CHIP_LOGO_PX}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain p-[3px]"
        onError={() => setPhase('fallback')}
        onAbort={() => setPhase('fallback')}
      />
    );
  }

  return (
    <img
      ref={hostedImgRef}
      src={hostedDefaultSrc}
      srcSet={hostedSrcset || undefined}
      sizes={hostedSrcset ? `${CHIP_LOGO_PX}px` : undefined}
      alt={alt}
      width={CHIP_LOGO_PX}
      height={CHIP_LOGO_PX}
      loading="lazy"
      decoding="async"
      className="h-full w-full object-contain p-[3px]"
      onLoad={onHostedLoad}
      onError={goPastHosted}
      onAbort={goPastHosted}
    />
  );
}

export interface SrpTopEmployersStripLabels {
  label: string;
  hubCta: string;
}

export interface SrpTopEmployersStripProps {
  employers: SrpTopEmployersStripItem[];
  locale: string;
  labels: SrpTopEmployersStripLabels;
  /** Show placeholder row while client fetch runs (no SSR seed). */
  isPending?: boolean;
}

const chipClass =
  'group/chip inline-flex min-h-9 max-w-[min(100%,14rem)] shrink-0 items-center gap-2 rounded-full ' +
  'bg-white/88 pl-1 pr-3 py-1 text-left shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_14px_rgba(0,0,0,0.05)] ' +
  'ring-1 ring-black/[0.06] backdrop-blur-md transition-[transform,box-shadow] duration-200 ease-out ' +
  'hover:shadow-[0_2px_4px_rgba(0,0,0,0.05),0_8px_22px_rgba(0,0,0,0.07)] active:scale-[0.97] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]/40 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-[#F8F5F0] ' +
  'dark:bg-[#2A1D0F]/95 dark:ring-[#4A3520] dark:shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_4px_20px_rgba(0,0,0,0.5)] ' +
  'dark:hover:bg-[#342415]/95 dark:hover:ring-[#5A4126] dark:hover:shadow-[0_1px_0_rgba(255,255,255,0.07)_inset,0_8px_28px_rgba(0,0,0,0.55)] ' +
  'dark:focus-visible:ring-[#98CFA8]/50 dark:focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#170F07]';

const hubLinkClass =
  'shrink-0 rounded-full px-2.5 py-1 text-[12px] sm:text-[13px] font-semibold tracking-tight ' +
  'text-[#007AFF] transition-[color,opacity,transform] duration-200 hover:opacity-80 active:scale-[0.97] ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007AFF]/40 focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-[#F8F5F0] ' +
  'dark:text-[#98CFA8] dark:hover:text-[#B6D4BC] dark:hover:opacity-100 ' +
  'dark:focus-visible:ring-[#98CFA8]/45 dark:focus-visible:ring-offset-[#170F07]';

/**
 * Compact SRP teaser: employers ranked by JobCrawls Employer Score (same source as /best-companies).
 * Chips sit in one row; when they exceed the width, the row scrolls horizontally (touch + pointer).
 */
export function SrpTopEmployersStrip({
  employers,
  locale,
  labels,
  isPending = false,
}: SrpTopEmployersStripProps): React.ReactElement | null {
  if (isPending && employers.length === 0) {
    return (
      <div
        className={
          'mb-3 sm:mb-4 w-full min-w-0 rounded-[20px] border border-black/[0.08] bg-white/50 px-3 py-2.5 ' +
          'shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)] backdrop-blur-2xl supports-[backdrop-filter]:bg-white/42 ' +
          'dark:border-[#4A3520] dark:bg-[#1F1509]/92 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_10px_40px_rgba(0,0,0,0.55)] ' +
          'dark:supports-[backdrop-filter]:bg-[#1F1509]/88 sm:px-4 sm:py-3.5'
        }
        role="status"
        aria-busy="true"
        aria-label={labels.label}
      >
        <div className="flex flex-col gap-2.5">
          <div className="flex items-start justify-between gap-3">
            <span className="h-3 w-28 animate-pulse rounded bg-[#DDD5C8] dark:bg-[#4A3520]" />
            <span className="h-3 w-40 max-w-[45%] animate-pulse rounded bg-[#DDD5C8]/80 dark:bg-[#342415]" />
          </div>
          <div className="scrollbar-hide min-w-0 overflow-x-hidden pb-1">
            <div className="-mx-1 flex w-max flex-nowrap gap-2 px-1">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <span
                  key={i}
                  className="inline-flex h-9 min-w-[6.5rem] shrink-0 animate-pulse rounded-full bg-[#F2EADF]/90 dark:bg-[#2A1D0F]"
                  aria-hidden
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!employers.length) return null;

  return (
    <div
      className={
        'mb-3 sm:mb-4 w-full min-w-0 rounded-[20px] border border-black/[0.08] bg-white/50 px-3 py-2.5 ' +
        'shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)] backdrop-blur-2xl supports-[backdrop-filter]:bg-white/42 ' +
        'dark:border-[#4A3520] dark:bg-[#1F1509]/92 dark:shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_10px_40px_rgba(0,0,0,0.55)] ' +
        'dark:supports-[backdrop-filter]:bg-[#1F1509]/88 sm:px-4 sm:py-3.5'
      }
      role="navigation"
      aria-label={labels.label}
    >
      <div className="flex flex-col gap-2.5">
        <div className="flex items-start justify-between gap-3">
          <p className="min-w-0 pt-0.5 text-[10px] font-semibold uppercase leading-tight tracking-[0.08em] text-black/45 dark:text-[#B8A48C]">
            {labels.label}
          </p>
          <Link
            href="/best-companies"
            locale={locale}
            className={hubLinkClass}
            onMouseEnter={prefetchBestCompaniesHubFromApi}
            onFocus={prefetchBestCompaniesHubFromApi}
          >
            {labels.hubCta}
          </Link>
        </div>

        <div className="scrollbar-hide min-w-0 overflow-x-auto overscroll-x-contain pb-1 touch-pan-x [-webkit-overflow-scrolling:touch]">
          <div className="-mx-1 flex w-max flex-nowrap gap-2 px-1">
            {employers.map((e) => {
              const score =
                typeof e.employerScore === 'number' && Number.isFinite(e.employerScore)
                  ? Math.round(e.employerScore)
                  : null;
              const chipTitle = score != null ? `${e.name} · ${score}/100` : e.name;
              return (
                <Link
                  key={e.companyId}
                  href={`/company/${encodeURIComponent(e.companyId)}`}
                  locale={locale}
                  className={chipClass}
                  title={chipTitle}
                >
                  <span className="pointer-events-none relative flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-b from-white to-black/[0.04] ring-1 ring-black/[0.07] dark:from-[#3D3529] dark:to-[#1F1509] dark:ring-[#4A3520]">
                    <SrpTopEmployerStripLogo companyId={e.companyId} name={e.name} logoUrl={e.logoUrl} />
                  </span>
                  <span className="flex min-w-0 flex-1 items-center gap-2">
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-tight tracking-tight text-black/[0.88] transition-colors group-hover/chip:text-black dark:text-[#F2EADF] dark:group-hover/chip:text-[#F2EADF]">
                      {e.name}
                    </span>
                    {score != null ? (
                      <span
                        className="shrink-0 rounded-md bg-black/[0.07] px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-black/70 dark:bg-[#4A3520] dark:text-[#C4A882]"
                        aria-label={`Employer score ${score} out of 100`}
                      >
                        {score}
                      </span>
                    ) : null}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
